import React from 'react';
import { css } from '@emotion/react';
import { i18n_earn_yoga_teaching_certificates } from '@yi/i18n/phrases/i18n_earn_yoga_teaching_certificates';
import { i18n_learn_ayurveda_and_yoga_philosophy } from '@yi/i18n/phrases/i18n_learn_ayurveda_and_yoga_philosophy';
import { i18n_practice_yoga_meditation_and_breathwork } from '@yi/i18n/phrases/i18n_practice_yoga_meditation_and_breathwork';
import { i18n_the_only_yoga_membership_you_ll_ever_need } from '@yi/i18n/phrases/i18n_the_only_yoga_membership_you_ll_ever_need';

import { SimpleResponsiveImage } from '~/src/common/image';
import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints, paddingVariables, typography } from '~/src/styles';
import { resetStyles } from '~/src/styles/reset-styles-mixins';

import { ReactComponent as Mandala } from './mandala.svg';
import { StartFreeTrialForm } from './StartFreeTrialForm';
import { commonStyles } from '../common-styles';

const POSE_IMG_SRC =
  'https://images.ctfassets.net/p0sybd6jir6r/4vNpoR9bMgYRCCyeMdR9Up/08bbcfdb315f6105da27d3d150ac1933/Pose_1_wo_borders.png';

const styles = {
  body: css({
    position: 'relative',
    display: 'grid',
    gridTemplateAreas: "'.' 'img' 'img' '.' 'title' '.' 'getStarted'",
    gridTemplateRows: `${paddingVariables.p16} 1fr 1fr ${paddingVariables.p24} auto  var(--SubHeading-Padding) auto`,
    background: 'white',
    paddingBottom: 'var(--Section-Padding)',
    [breakpoints.minTablet]: {
      gridTemplateAreas: "'img' 'img' '.' 'title' '.' 'details' '.' 'getStarted'",
      gridTemplateRows: `1fr 1fr ${paddingVariables.p40} auto 24px auto 56px auto`,
    },
    [breakpoints.minDesktop]: css(commonStyles.containerMaxWidth, {
      position: 'static',
      gridTemplateAreas:
        "'title title title' 'title title title' '. . .' 'details details .' '. . .' 'getStarted . .'",
      gridTemplateRows: `1fr 1fr ${paddingVariables.p80} auto ${paddingVariables.p80} auto`,
      gridTemplateColumns: '625px 50px 1fr',
      margin: 'auto',
      paddingTop: 'var(--Section-Padding)',
    }),
  }),
  imageContainer: css({
    gridArea: 'img',
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: '80%',
    margin: 'auto',
    paddingBottom: '60px',
    [breakpoints.minTablet]: {
      maxWidth: '60%',
    },
    [breakpoints.minDesktop]: {
      gridArea: 'unset',
      gridColumnStart: 2,
      gridColumnEnd: -1,
      gridRowStart: 2,
      gridRowEnd: -1,
      justifySelf: 'end',
      maxWidth: 'unset',
    },
  }),
  poseImg: css({
    top: '60px',
    width: '100%',
    zIndex: '10',
    margin: 'auto',
    position: 'relative',
  }),
  mandalaImg: css({
    width: '75%',
    zIndex: '5',
    margin: 'auto',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0.5,
  }),
  title: css(typography.heading1, commonStyles.contentPadding, {
    zIndex: 10,
    color: 'var(--Dark-Text, #323439)',
    gridArea: 'title',
  }),
  detailsContainer: css(commonStyles.contentPadding, {
    display: 'none',
    [breakpoints.minTablet]: {
      zIndex: 10,
      gridArea: 'details',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
  }),
  detailsItem: css(resetStyles.heading, typography.label, {
    zIndex: 10,
    color: 'var(--Grey-Text, #585858)',
    [breakpoints.minTablet]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
  }),
  getStartedContainer: css(commonStyles.contentPadding, {
    zIndex: 10,
    gridArea: 'getStarted',
  }),
  background: css({
    borderRadius: '0px 0px var(--Border-Radius) var(--Border-Radius)',
    gridColumnStart: 1,
    gridColumnEnd: -1,
    gridRowStart: 1,
    gridRowEnd: 3,
    background: 'var(--Primary-Background, rgba(255, 243, 226, 0.50))',
    zIndex: 1,
    [breakpoints.minTablet]: {
      gridRowEnd: 2,
    },
    [breakpoints.minDesktop]: {
      display: 'none',
    },
  }),
};

export const Hero: React.FC = () => {
  const { locale } = useLocale();
  return (
    <div css={styles.body}>
      <div css={styles.background} />
      <div css={styles.imageContainer}>
        <SimpleResponsiveImage css={styles.poseImg} src={POSE_IMG_SRC} width={700} />
        <Mandala css={styles.mandalaImg} />
      </div>
      <h1 css={styles.title}>{i18n_the_only_yoga_membership_you_ll_ever_need(locale)}</h1>
      <div css={styles.detailsContainer}>
        <h2 css={styles.detailsItem}>{i18n_practice_yoga_meditation_and_breathwork(locale)}</h2>
        <h2 css={styles.detailsItem}>{i18n_learn_ayurveda_and_yoga_philosophy(locale)}</h2>
        <h2 css={styles.detailsItem}>{i18n_earn_yoga_teaching_certificates(locale)}</h2>
      </div>
      <StartFreeTrialForm css={styles.getStartedContainer} />
    </div>
  );
};
